import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { catchError, finalize, tap } from "rxjs/operators";
import { Observable, of } from "rxjs";
import { validateFiles } from "../../shared-modules/file-upload-widget/file-upload-widget.util";
import { ServerTokenService } from "../../services/server-token.service";
import { FacilitySettings, FacilitySettingsExtended, FacilitySettingsColumnConfig } from "./facilities-settings.interface";
import { ERRORS, COLUMN_CONFIGS, UPLOAD_CONFIG, CHECKBOX_CONFIG_DEFAULT, TEXTINPUT_CONFIG_DEFAULT } from "./facilities-settings.constants";
import { FormFieldOnChangeEvent } from "../../interface";
import { parseCsvFile } from "../../utils";

@Component({
  selector: 'facilities-settings',
  templateUrl: './facilities-settings.component.html',
  styleUrls: ['./facilities-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FacilitiesSettingsComponent implements OnInit {

  facilitySettings: FacilitySettingsExtended[] = [];

  columnConfigs: FacilitySettingsColumnConfig[] = [ ...COLUMN_CONFIGS ];

  generalError = '';
  
  dataLoading = false;

  templateMap: Record<string, TemplateRef<any>> = {};

  readonly UPLOAD_CONFIG = UPLOAD_CONFIG;
  readonly CHECKBOX_CONFIG_DEFAULT = CHECKBOX_CONFIG_DEFAULT;
  readonly TEXTINPUT_CONFIG_DEFAULT = TEXTINPUT_CONFIG_DEFAULT;

  @ViewChild('valueTpl', { static: true }) valueTpl!: TemplateRef<any>;
  @ViewChild('checkboxTpl', { static: true }) checkboxTpl!: TemplateRef<any>;

  constructor(
    private server: ServerTokenService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.initData();
  }

  ngAfterViewInit(): void {
    this.templateMap = {
      valueTpl: this.valueTpl,
      checkboxTpl: this.checkboxTpl,
    };
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files[0]) {
      return;
    }
    const error = validateFiles(input.files, UPLOAD_CONFIG);
    if (error) {
      this.generalError = error;
      return;
    }
    this.uploadCsvData(input.files[0]);
  }

  onValueChange({ value }: FormFieldOnChangeEvent, columnKey: string, index: number): void {
    const updateRow = this.facilitySettings[index];
    if (!updateRow) {
      return;
    }
    this.updateField({ 
      _id: updateRow._id, 
      [columnKey]: value,
    });
  }

  private initData(): void {
    this.getData().pipe(
      finalize(() => this.cdr.markForCheck()),
    ).subscribe();
  }

  private getData(): Observable<FacilitySettingsExtended[]> {
    this.dataLoading = true;
    return this.server.getWithToken<FacilitySettingsExtended[]>('snfs/admin/getFacilitySettings').pipe(
      tap(facilitySettings => {
        this.facilitySettings = facilitySettings;
      }),
      catchError(error => {
        console.error(error);
        this.generalError = ERRORS.GET;
        return of(null);
      }),
      finalize(() => {
        this.dataLoading = false;
        this.cdr.markForCheck();
      })
    );
  }

  private async uploadCsvData(csvFile: File): Promise<void> {
    const csvData = await parseCsvFile(csvFile);
    this.server.postWithToken<FacilitySettingsExtended[]>('snfs/admin/importFacilitiesFromSql', csvData).pipe(
      tap(facilitySettings => {
        this.facilitySettings = facilitySettings;
      }),
      catchError(error => {
        console.error(error);
        this.generalError = ERRORS.IMPORT;
        return of(null);
      }),
      finalize(() => {
        this.cdr.markForCheck();
      })
    ).subscribe();
  }

  private async updateField(rowToUpdate: Partial<FacilitySettings>): Promise<void> {
    this.server.postWithToken<FacilitySettings>('snfs/admin/updateFacilitySettings', { rowToUpdate }).pipe(
      tap(updatedRow => {
        this.facilitySettings = this.facilitySettings.map(row => {
          if (row._id === updatedRow._id) {
            return { ...row, ...updatedRow };
          }
          return row;
        });
      }),
      catchError(error => {
        console.error(error);
        this.generalError = ERRORS.UPDATE;
        return of(null);
      }),
      finalize(() => {
        this.cdr.markForCheck();
      })
    ).subscribe();
  }

  trackById (_: number, row: FacilitySettingsExtended) {
    return row._id
  }

  trackByKey (_: number, column: FacilitySettingsColumnConfig) {
    return column.label;
  }

  get tableLoading(): boolean {
    return this.dataLoading;
  }
}